import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Popover,
  Avatar,
  Badge,
  Button,
  Chip,
  ListItemButton,
} from '@mui/material';
import { Bell, Check, Trash } from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAvatarSrc } from '../../utils/avatarConstants';
import { navigateFromNotification } from '../../utils/notificationNavigation';

// Icon size determines drawer width - square icons with padding
const ICON_SIZE = 24;
const ICON_PADDING = 12; // Padding around icon (6px on each side)
const DRAWER_WIDTH = ICON_SIZE + (ICON_PADDING * 2) + 16; // Icon + padding + extra space = 64px

const EXPANDED_WIDTH = DRAWER_WIDTH;
const COLLAPSED_WIDTH = DRAWER_WIDTH;

// Function to shorten navigation labels
const shortenLabel = (title) => {
  const labelMap = {
    'Dashboard': 'Home',
    'Business Profile': 'Profile',
    'Wedding Details': 'Wedding',
    'Couple Details': 'Couple',
    'Stationary & Gifts': 'Stationary',
    'Staff Management': 'Staff',
    'My Couples': 'Couples',
    'To-Do List': 'To-Do',
    'My Bookings': 'Bookings',
  };
  return labelMap[title] || title;
};

function SidebarNavigation({
  sections,
  userProfile,
  onProfileMenuOpen,
  logoComponent,
  logoIconComponent,
  isActive,
  isSupplier = false,
  // Notification props
  notifications = [],
  unreadCount = 0,
  unreadMessagesCount = 0,
  onMarkAsRead,
  onMarkAllAsRead,
  onDeleteAllNotifications,
  onDeleteNotification,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, _setOpenMenus] = useState({});
  const [submenuAnchor, setSubmenuAnchor] = useState(null);
  const [submenuItem, setSubmenuItem] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const submenuAnchorRef = useRef(null);

  // Always collapsed (icon-only) - no collapse functionality
  const isCollapsed = true;


  const handleItemClick = useCallback((item, event) => {
    event.stopPropagation();
    
    if (item.children && item.children.length > 0) {
      // Show submenu popover for icon-only view
      setSubmenuAnchor(event.currentTarget);
      setSubmenuItem(item);
      submenuAnchorRef.current = event.currentTarget;
    } else if (item.path) {
      navigate(item.path);
      setSubmenuAnchor(null);
      setSubmenuItem(null);
      setNotificationAnchor(null);
    }
  }, [navigate]);

  const handleSubmenuClose = useCallback(() => {
    setSubmenuAnchor(null);
    setSubmenuItem(null);
  }, []);

  const handleNotificationClose = useCallback(() => {
    setNotificationAnchor(null);
  }, []);

  const handleNotificationItemClick = useCallback(async (notification) => {
    if (!notification.isRead && onMarkAsRead) {
      await onMarkAsRead(notification._id);
    }
    
    navigateFromNotification(navigate, notification, { isSupplier });

    handleNotificationClose();
  }, [onMarkAsRead, navigate, handleNotificationClose, isSupplier]);

  const getPriorityColor = useCallback((priority) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  }, []);

  const formatTimeAgo = useCallback((date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString();
  }, []);

  const handleSubmenuItemClick = useCallback((path) => {
    navigate(path);
    handleSubmenuClose();
  }, [navigate, handleSubmenuClose]);

  const isMenuOpen = useCallback((title) => {
    return openMenus[title] || false;
  }, [openMenus]);

  const renderMenuItem = useCallback((item, index) => {
    const hasChildren = item.children && item.children.length > 0;
    // Check if active - either direct path match or any child path matches
    let active = false;
    if (isActive) {
      if (item.path) {
        active = isActive(item.path);
      } else if (hasChildren) {
        active = item.children.some((child) => child.path && isActive(child.path));
      }
    }
    
    // Special handling for Notifications item
    const isNotificationItem = item.title === 'Notifications';
    const showBadge = isNotificationItem && unreadCount > 0;
    const isMessagesItem = item.title === 'Messages';
    const showMessagesDot = isMessagesItem && unreadMessagesCount > 0;

    // Icon with label below menu item
    const shortLabel = shortenLabel(item.title);
    const menuItemContent = (
      <Tooltip
        key={item.title || index}
        title={item.title}
        placement="right"
        enterDelay={200}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 0.5,
            width: '100%',
            maxWidth: '100%',
            textAlign: 'center',
          }}
          onClick={(e) => handleItemClick(item, e)}
        >
          <ListItemButton
            onClick={(e) => handleItemClick(item, e)}
            data-navigation="true"
            data-active={active}
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40, // Match bottom icon logo size
              height: 40, // Match bottom icon logo size
              maxWidth: 40,
              minWidth: 40,
              borderRadius: '8px',
              p: 0,
              backgroundColor: active ? undefined : 'transparent',
              background: active ? 'radial-gradient(circle, rgba(255, 255, 255, 1) 10%, rgba(255, 255, 255, 1) 36%, rgba(255, 255, 255, 0) 80%)' : undefined,
              '&:hover': {
                backgroundColor: active ? undefined : 'transparent',
                background: active ? 'radial-gradient(circle, rgba(255, 255, 255, 1) 10%, rgba(255, 255, 255, 1) 36%, rgba(255, 255, 255, 0) 80%)' : undefined,
                opacity: active ? 1 : 0.7,
              },
            }}
          >
            <ListItemIcon 
              data-navigation="true"
              sx={{
                minWidth: 0,
                marginRight: 0,
                paddingRight: 0,
                margin: 0,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: active ? '#e7548b' : 'text.primary',
                '& svg': {
                  display: 'block',
                  margin: 0,
                  width: '20px', // Smaller icon
                  height: '20px',
                },
              }}
            >
              {isNotificationItem && showBadge ? (
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  {item.icon}
                </Badge>
              ) : showMessagesDot ? (
                <Badge variant="dot" color="error">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
          </ListItemButton>
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: '0.5rem', // 8px - small label
              fontWeight: 500, // Increased font weight
              lineHeight: 1.2,
              textAlign: 'center',
              color: 'text.primary', // Always dark, regardless of active state
              m: 0, // Remove all margins
              p: 0, // Remove all padding
              whiteSpace: 'nowrap',
              overflow: 'visible',
              textOverflow: 'clip',
              width: '100%',
              display: 'block',
            }}
          >
            {shortLabel}
          </Typography>
        </Box>
      </Tooltip>
    );

    // For items with children, show popover on click
    if (hasChildren) {
      return menuItemContent;
    }

    return menuItemContent;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [isCollapsed, isActive, isMenuOpen, handleItemClick, unreadCount, unreadMessagesCount, location]);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      width: '100%', 
      maxWidth: '100%', 
      overflow: 'hidden',
      backgroundColor: 'transparent', // No background
    }}>
      {/* Header - Logo at top, centered */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 64,
        width: '100%',
        boxSizing: 'border-box',
      }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            cursor: 'pointer',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            maxWidth: '100%',
          }}
          onClick={() => navigate(isSupplier ? '/supplier/dashboard' : '/dashboard')}
        >
          {logoIconComponent || logoComponent}
        </Box>
      </Box>

      {/* Menu Sections - Centered icons only */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'hidden', // Prevent scroll
        py: 2, 
        width: '100%', 
        maxWidth: '100%', 
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
      }}>
        {sections.filter(section => section.items && section.items.length > 0).map((section, sectionIndex) => (
          <React.Fragment key={section.title || sectionIndex}>
            <List 
              sx={{ 
                px: 0, 
                width: '100%', 
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
              }}
              data-navigation-list="true"
            >
              {section.items.map((item, index) => renderMenuItem(item, index))}
            </List>
            {sectionIndex < sections.filter(s => s.items && s.items.length > 0).length - 1 && (
              <Divider 
                sx={{ 
                  width: '80%',
                  my: 1,
                }} 
              />
            )}
          </React.Fragment>
        ))}
      </Box>

      {/* User Profile Section - Avatar at bottom, centered */}
      {userProfile && (
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
          onClick={(e) => onProfileMenuOpen && onProfileMenuOpen(e)}
        >
          <Avatar 
            src={getAvatarSrc(userProfile.profilePicture || userProfile.avatar)}
            alt={userProfile.name || `${userProfile.firstName} ${userProfile.lastName}`}
            sx={{ 
              width: 40, 
              height: 40,
              borderRadius: '10px',
              backgroundColor: '#ffffff',
            }}
          >
            {!userProfile.profilePicture && !userProfile.avatar && (
              userProfile.firstName ? 
                userProfile.firstName[0] : 
                userProfile.name?.[0] || userProfile.email?.[0]?.toUpperCase() || 'U'
            )}
          </Avatar>
        </Box>
      )}

      {/* Submenu Popover */}
      <Popover
        open={Boolean(submenuAnchor)}
        anchorEl={submenuAnchor}
        onClose={handleSubmenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            mt: 0.5,
            boxShadow: 3,
          }
        }}
      >
        {submenuItem && submenuItem.children && (
          <List sx={{ p: 1 }}>
            {submenuItem.children.map((child) => {
              const childActive = isActive ? isActive(child.path) : false;
              return (
                <ListItem
                  key={child.title}
                  onClick={() => handleSubmenuItemClick(child.path)}
                  data-navigation="true"
                  data-navigation-child="true"
                  data-active={childActive}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    cursor: 'pointer',
                  }}
                >
                  <ListItemText 
                    primary={child.title} 
                    primaryTypographyProps={{ noWrap: true }} 
                    data-navigation="true"
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Popover>

      {/* Notification Popover */}
      <Popover
        open={Boolean(notificationAnchor)}
        anchorEl={notificationAnchor}
        onClose={handleNotificationClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
            mt: 0.5,
            boxShadow: 3,
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Notifications</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {unreadCount > 0 && onMarkAllAsRead && (
                <Button
                  size="small"
                  onClick={onMarkAllAsRead}
                  startIcon={<Check size={16} />}
                >
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && onDeleteAllNotifications && (
                <Button
                  size="small"
                  color="error"
                  onClick={onDeleteAllNotifications}
                  startIcon={<Trash size={16} />}
                >
                  Delete all
                </Button>
              )}
            </Box>
          </Box>

          {notifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Bell size={48} weight="regular" color="#ccc" />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No notifications yet
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.slice(0, 10).map((notification, index) => (
                <React.Fragment key={notification._id}>
                  <ListItem
                    sx={{
                      p: 0,
                      backgroundColor: notification.isRead ? 'transparent' : 'action.hover',
                      mb: 1,
                      opacity: notification.isRead ? 0.4 : 1,
                      transition: 'opacity 0.2s ease',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleNotificationItemClick(notification)}
                      sx={{
                        p: 2,
                        '&:hover': {
                          backgroundColor: 'action.selected'
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: notification.isRead ? 'normal' : 'bold',
                                flex: 1
                              }}
                            >
                              {notification.title}
                            </Typography>
                            {notification.priority === 'high' && (
                              <Chip
                                label="High"
                                size="small"
                                color={getPriorityColor(notification.priority)}
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 0.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}
                            >
                              {notification.message}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {formatTimeAgo(notification.createdAt)}
                            </Typography>
                          </Box>
                        }
                      />
                      
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.25 }}>
                        {!notification.isRead && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: 'primary.main'
                            }}
                          />
                        )}
                        {!notification.isRead && onMarkAsRead && (
                          <IconButton
                            size="medium"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await onMarkAsRead(notification._id);
                            }}
                            sx={{ opacity: 0.8, p: 0.6, '&:hover': { opacity: 1 } }}
                          >
                            <Check size={18} />
                          </IconButton>
                        )}
                        {onDeleteNotification && (
                          <IconButton
                            size="medium"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteNotification(notification._id);
                            }}
                            sx={{ opacity: 0.8, p: 0.6, '&:hover': { opacity: 1 } }}
                          >
                            <Trash size={18} />
                          </IconButton>
                        )}
                      </Box>
                    </ListItemButton>
                  </ListItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}

          {notifications.length > 10 && (
            <Box sx={{ textAlign: 'center', pt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  handleNotificationClose();
                  navigate(isSupplier ? '/supplier/notifications' : '/notifications');
                }}
              >
                View all notifications
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
}

export default React.memo(SidebarNavigation);
export { EXPANDED_WIDTH, COLLAPSED_WIDTH };

